import React, { useEffect, useMemo } from 'react';

import { observer } from 'mobx-react';
import { Pagination, Container } from '@mui/material';

import classes from './NgzCoachList.module.scss';
import { angularizeWithProviders } from '../../../shared/providers';
import { EmptyList } from '../../../shared/components/ui-components';
import { CoachListPageTitle } from './CoachListPageTitle/CoachListPageTitle';
import { TitleRow } from './TitleRow/TitleRow';
import { FilterRow } from './FilterRow/FilterRow';
import { CoachRow } from './CoachRow/CoachRow';
import {
  COACH_LIST_COLUMN,
  COACH_LIST_MODE,
  CoachListMode,
  ShowRowsConfig,
} from './NgzCoachList.settings';
import { loggedInUserDataStore } from '../../../shared/stores/loggedInUserDataStore';
import { useUserRoleLabel } from '../../../shared/hooks/useUserRoleLabel';
import { useCoachListStore } from './hooks/useCoachListStore';

type NgzCoachListProps = {
  listMode: CoachListMode;
};

const NgzCoachList = observer(({ listMode }: NgzCoachListProps) => {
  const { isSuperAdmin, paymentFlowType } = loggedInUserDataStore;
  const { coachList, getCoachList, filterMethods } = useCoachListStore(listMode);

  const { getForAgent } = useUserRoleLabel();

  const { isShowPlaceholder, isSearchUsed, isShowPagination, getPaginationProps } =
    filterMethods;

  useEffect(() => {
    getCoachList();
  }, []);

  const paginationProps = getPaginationProps();

  const showRowsConfig = useMemo<ShowRowsConfig>(
    () => ({
      [COACH_LIST_COLUMN.NAME]: true,
      [COACH_LIST_COLUMN.ENTITY]: isSuperAdmin,
      [COACH_LIST_COLUMN.STATE]: isSuperAdmin,
      [COACH_LIST_COLUMN.CITY]: isSuperAdmin,
      [COACH_LIST_COLUMN.SETUP_COMMISSION]:
        listMode === COACH_LIST_MODE.NUTRITION &&
        (isSuperAdmin || !paymentFlowType.NEW_USER_FLOW_2024),
      [COACH_LIST_COLUMN.RENEW_COMMISSION]:
        listMode === COACH_LIST_MODE.NUTRITION &&
        (isSuperAdmin || !paymentFlowType.NEW_USER_FLOW_2024),
      [COACH_LIST_COLUMN.COACH_DESIGNATION]: true,
      [COACH_LIST_COLUMN.STATUS]: true,
      [COACH_LIST_COLUMN.EXTRA_INFO]: true,
    }),
    [listMode, isSuperAdmin, paymentFlowType.NEW_USER_FLOW_2024],
  );

  const indicatorColumnsCount = Object.values(showRowsConfig).filter(Boolean).length - 2;

  return (
    <Container>
      <CoachListPageTitle />
      <div
        className={classes.coachTable}
        style={{
          display: 'grid',
          gridTemplateColumns: `1.25fr repeat(${indicatorColumnsCount}, 1fr) auto`,
        }}
      >
        <TitleRow showRowsConfig={showRowsConfig} />
        <FilterRow showRowsConfig={showRowsConfig} listMode={listMode} />
        {coachList.map((coachInfo) => (
          <CoachRow
            key={coachInfo.id}
            coachInfo={coachInfo}
            showRowsConfig={showRowsConfig}
          />
        ))}
      </div>
      <EmptyList
        show={isShowPlaceholder}
        isSearch={isSearchUsed}
        description={`There are no ${getForAgent({ pluralize: true, case: 'lower' })}`}
      />
      {isShowPagination && <Pagination {...paginationProps} />}
    </Container>
  );
});

angularizeWithProviders(NgzCoachList, 'ngzCoachList', {
  listMode: '@',
});

export { NgzCoachList };
